import React, { useState } from "react";
import { LineSegment, VictoryAxis, VictoryBar, VictoryChart, VictoryContainer, VictoryLabel } from "victory";
import { colors, fonts } from "../../Styles";
import _ from "lodash";
import { t } from "i18next";
import NewSelect, { MenuFloats } from "../BaseComponents/newSelect";
import { NewFieldStyles } from "../../Definitions/newFieldStyles";
import { Consumption } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";

interface Last10DaysViewer_Props {
  data?: Consumption[];
}

export default function Last10DaysViewer({ data }: Last10DaysViewer_Props) {
  const [leftId, setLeftId] = useState<number | undefined>(2);
  const [rightId, setRightId] = useState<number | undefined>();

  // #region | SelectFields
  const selectedValue = [
    {
      label: t("locationFeederPages.sections.activations", {
        defaultValue: "Activations",
      }),
      id: 1,
    },
    {
      label: `${t("feed.general.feed", { defaultValue: "Feed" })} (${t(
        "units.kg",
        { defaultValue: "KG" }
      )})`,
      id: 2,
    },
  ];

  const handleSelectChange = (side: "left" | "right") => (e: any) => {
    const selectedId = e?.id; // Tjek om 'e' har en id
    if (side === "left" && selectedId !== undefined) {
      setLeftId(selectedId);
    } else if (side === "right" && selectedId !== undefined) {
      setRightId(selectedId);
    } else if (side === "left" && selectedId === undefined) {
      setLeftId(undefined);
    } else if (side === "right" && selectedId === undefined) {
      setRightId(undefined);
    }
  };

  const rightSelectField = (
    <NewSelect
      isClearable
      valueOverrides={{
        defaultValue: selectedValue.find((option) => option.id === rightId),
      }}
      options={selectedValue}
      onChange={handleSelectChange("right")}
      fieldOverrides={{
        fieldStyle: NewFieldStyles.greyTertiary,
        menuFloat: MenuFloats.left,
        fieldWidth: "200px",
      }}
    />
  );

  const leftSelectField = (
    <NewSelect
      isClearable
      options={selectedValue}
      onChange={handleSelectChange("left")}
      fieldOverrides={{
        fieldStyle: NewFieldStyles.greyTertiary,
        fieldWidth: "200px",
      }}
      valueOverrides={{
        defaultValue: selectedValue.find((option) => option.id === leftId),
      }}
    />
  );

  // #endregion

  // #region | Data
  const consumptionData = data ?? [];

  // const activationsRange = [
  //   Math.min(...TestData?.map((d) => d.activations)) - 10,
  //   Math.max(...TestData.map((d) => d.activations)),
  // ];

  const totals = consumptionData.map((d) => d.total ?? 0);
  const feedTotalRange = [0, Math.max(...totals) / 1000];

  const ticks = 6;
  const tickValues = _.range(ticks + 1);

  const today = new Date();

  const consumptionDataWithDates = consumptionData
    .slice(0, 10)
    .map((d, index) => ({
      ...d,
      date: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - index
      ),
    }));

  const tickValuesX = consumptionDataWithDates.map((d) => d.date);

  const tickFormatFeed = (range: number[]) => (t: number) =>
    (range[0] + t * ((range[1] - range[0]) / ticks)).toFixed(2);

  // const tickFormatActivations = (range: number[]) => (t: number) =>
  //   Math.round(range[0] + t * ((range[1] - range[0]) / ticks));

  const normalize =
    (range: number[], props: string) => (datum: { [x: string]: number }) =>
      ((datum[props] ?? 0) / 1000 - range[0]) / ((range[1] - range[0]) / ticks);

  const domain = { y: [0, ticks] as [number, number] };
  // #endregion

  // #region | Bars
  const feedTotalBars =
    rightId === 2 || leftId === 2 ? (
      <VictoryBar
        data={consumptionDataWithDates}
        x="date"
        y={normalize(feedTotalRange, "total")}
        style={{
          data: {
            width: 20,
            fill: colors.greyLightPrimary,
          },
        }}
      />
    ) : (
      <></>
    );

  const feedConsumptionBars =
    rightId === 2 || leftId === 2 ? (
      <VictoryBar
        data={consumptionDataWithDates}
        x="date"
        y={normalize(feedTotalRange, "progressed")}
        style={{
          data: {
            width: 20,
            fill: colors.graphBlueEight,
          },
        }}
      />
    ) : (
      <></>
    );

  // const activationsBars =
  //   rightId === 1 || leftId === 1 ? (
  //     <VictoryScatter
  //       data={data.slice(0, 10)}
  //       size={9}
  //       x="date"
  //       y={normalize(activationsRange, "activations")}
  //       style={{
  //         data: {
  //           fill: colors.graphActivity,
  //         },
  //       }}
  //       labels={TestData.map((d) => d.activations)}
  //       labelComponent={
  //         <VictoryLabel
  //           style={{
  //             ...fonts.header,
  //             ...fonts.fontSize5,
  //             fill: colors.whitePrimary,
  //           }}
  //           dy={5}
  //         />
  //       }
  //     />
  //   ) : (
  //     <></>
  //   );
  // #endregion

  // #region | Y-axis
  const leftAxis =
    leftId === 1 || leftId === 2 ? (
      <VictoryAxis
        dependentAxis
        style={{
          ticks: { stroke: colors.greyPrimary, size: 5 },
          axis: { stroke: colors.greyPrimary, strokeWidth: 2 },
        }}
        offsetX={30}
        tickValues={tickValues}
        axisComponent={<LineSegment y1={26} y2={255} />}
        orientation="left"
        tickFormat={
          leftId === 2 ? tickFormatFeed(feedTotalRange) : undefined // tickFormatActivations(activationsRange)
        }
        tickLabelComponent={
          <VictoryLabel
            text={
              ({ datum }) =>
                datum === tickValues[0]
                  ? ""
                  : leftId === 2
                  ? tickFormatFeed(feedTotalRange)(datum)
                  : "" // undefinedtickFormatActivations(activationsRange)(datum)
            }
            style={{
              ...fonts.paragraph,
              ...fonts.fontSize5,
              fill: colors.greyPrimary,
            }}
          />
        }
      />
    ) : (
      <></>
    );

  const rightAxis =
    rightId === 1 || rightId === 2 ? (
      <VictoryAxis
        dependentAxis
        style={{
          ticks: { stroke: colors.greyPrimary, size: 5 },
          axis: { stroke: colors.greyPrimary, strokeWidth: 2 },
        }}
        offsetX={30}
        tickValues={tickValues}
        axisComponent={<LineSegment y1={26} y2={255} />}
        orientation="right"
        tickFormat={
          rightId === 2 ? tickFormatFeed(feedTotalRange) : undefined // tickFormatActivations(activationsRange)
        }
        tickLabelComponent={
          <VictoryLabel
            text={
              ({ datum }) =>
                datum === tickValues[0]
                  ? ""
                  : rightId === 2
                  ? tickFormatFeed(feedTotalRange)(datum)
                  : "" // tickFormatActivations(activationsRange)(datum)
            }
            style={{
              ...fonts.paragraph,
              ...fonts.fontSize5,
              fill: colors.greyPrimary,
            }}
          />
        }
      />
    ) : (
      <></>
    );

  // #endregion

  return (
    <div
      style={{
        display: "grid",
        gridAutoColumns: "1fr 1fr",
        gridAutoRows: "1fr 1fr",
        width: "650px",
        height: "375px",
      }}
    >
      <div
        style={{
          gridArea: "1/1/2/2",
          display: "flex",
          justifyContent: "start",
          zIndex: "2",
        }}
      >
        {leftSelectField}
      </div>

      <div
        style={{
          gridArea: "1/2/2/3",
          display: "flex",
          justifyContent: "end",
          zIndex: "2",
        }}
      >
        {rightSelectField}
      </div>

      <div style={{ gridArea: "2/1/3/3", marginTop: "-20px", zIndex: "1" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <div style={{ width: "650px", height: "350px", margin: "0 auto" }}>
            <VictoryChart
              domain={domain}
              width={440}
              height={300}
              containerComponent={<VictoryContainer width={450} height={300} />}
            >
              {feedTotalBars}
              {feedConsumptionBars}
              {/* {activationsBars} */}

              {leftAxis}
              {rightAxis}

              {/* X-Axis: Last 10 Days */}
              <VictoryAxis
                tickValues={tickValuesX}
                tickFormat={(t: Date) =>
                  new Date(t).toLocaleDateString("da-DK", {
                    day: "2-digit",
                    month: "2-digit",
                  })
                }
                standalone={false}
                style={{
                  ticks: { stroke: colors.greyPrimary, size: 5 },
                  axis: { stroke: colors.greyPrimary, strokeWidth: 2 },
                }}
                crossAxis={false}
                tickLabelComponent={
                  <VictoryLabel
                    dy={-3}
                    style={{
                      ...fonts.paragraph,
                      ...fonts.fontSize5,
                      fill: colors.greyPrimary,
                    }}
                  />
                }
                axisComponent={<LineSegment x1={0} x2={440} />}
              />
            </VictoryChart>
          </div>
          <div
            style={{
              marginTop: "-20px",
              display: "flex",
              flexDirection: "row",
              gap: "10px",
            }}
          >
            {(leftId === 2 || rightId === 2) && (
              <div
                style={{ display: "flex", flexDirection: "row", gap: "10px" }}
              >
                <div
                  style={{
                    padding: "4px 5px",
                    ...fonts.header,
                    ...fonts.fontSize4,
                    color: colors.greyDarkPrimary,
                    backgroundColor: colors.greyLightPrimary,
                    borderRadius: "5px",
                  }}
                >
                  {t("feed.general.fedTotal", { defaultValue: "Fed Total" })}
                </div>
                <div
                  style={{
                    padding: "4px 5px",
                    ...fonts.header,
                    ...fonts.fontSize4,
                    color: colors.whitePrimary,
                    backgroundColor: colors.graphBlueEight,
                    borderRadius: "5px",
                  }}
                >
                  {t("feed.general.feedConsumption", {
                    defaultValue: "Feed Consumption",
                  })}
                </div>
              </div>
            )}
            {(leftId === 1 || rightId === 1) && (
              <div
                style={{
                  padding: "4px 5px",
                  ...fonts.header,
                  ...fonts.fontSize4,
                  color: colors.whitePrimary,
                  backgroundColor: colors.graphActivity,
                  borderRadius: "5px",
                }}
              >
                {t("locationFeederPages.sections.activations", {
                  defaultValue: "Activations",
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
